import { Component } from '@theme/component';
import {
  ThemeEvents,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import {
  StandardEvents,
  ProductSelectEvent,
  CartLinesUpdateEvent,
  CartErrorEvent,
} from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id]
 * @property {string} [title]
 * @property {string} [name]
 * @property {boolean} [available]
 * @property {Object} [featured_media]
 * @property {Object} [featured_media.preview_image]
 * @property {string} [featured_media.preview_image.src]
 * @property {string} [featured_media.alt]
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 * @property {HTMLImageElement} [productImage]
 */

/**
 * Sticky add-to-cart component.
 *
 * Desktop:
 * Shows after the original buy-buttons block has moved above the viewport.
 *
 * Mobile:
 * Shows before the original buy-buttons block enters the viewport,
 * hides while the original buttons are visible,
 * then shows again after the original buttons have been scrolled past.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
  ];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {HTMLElement | null} */
  #buyButtonsBlock = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartError,
      this.#handleCartAddComplete,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handleQuantityUpdate,
      { signal }
    );

    window.addEventListener(
      'resize',
      this.#handleViewportChange,
      { signal }
    );

    window.addEventListener(
      'orientationchange',
      this.#handleViewportChange,
      { signal }
    );

    this.#getInitialQuantity();
    this.#syncExpressCheckoutInputs();
    this.#initializeAcceleratedCheckout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#abortController.abort();

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Watches the original buy-buttons block.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();

    if (!productForm) {
      return;
    }

    const buyButtonsBlock =
      productForm.closest('.buy-buttons-block');

    if (!buyButtonsBlock) {
      return;
    }

    this.#buyButtonsBlock =
      /** @type {HTMLElement} */ (
        buyButtonsBlock
      );

    this.#targetAddToCartButton =
      productForm.querySelector(
        '[ref="addToCartButton"]'
      );

    this.#buyButtonsIntersectionObserver =
      new IntersectionObserver(() => {
        this.#syncStickyVisibility();
      }, {
        threshold: [0, 0.01, 0.1, 0.5, 1],
      });

    this.#buyButtonsIntersectionObserver.observe(
      buyButtonsBlock
    );

    window.requestAnimationFrame(() => {
      this.#syncStickyVisibility();
    });
  }

  /**
   * Main visibility logic.
   *
   * Desktop:
   * - Hidden before buy buttons
   * - Hidden while buy buttons are visible
   * - Visible after buy buttons are scrolled past
   *
   * Mobile:
   * - Visible before buy buttons enter viewport
   * - Hidden while buy buttons are visible
   * - Visible after buy buttons are scrolled past
   */
  #syncStickyVisibility = () => {
    if (!this.#buyButtonsBlock) {
      return;
    }

    const rect =
      this.#buyButtonsBlock.getBoundingClientRect();

    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight;

    const isMobile =
      window.matchMedia(
        '(max-width: 749px)'
      ).matches;

    const isVisibleInViewport =
      rect.top < viewportHeight &&
      rect.bottom > 0;

    const buttonsAreBelowViewport =
      rect.top >= viewportHeight;

    const buttonsAreAboveViewport =
      rect.bottom <= 0;

    if (isMobile) {
      if (
        buttonsAreBelowViewport ||
        buttonsAreAboveViewport
      ) {
        this.#showStickyBar();
        return;
      }

      if (isVisibleInViewport) {
        this.#hideStickyBar();
        return;
      }

      this.#hideStickyBar();
      return;
    }

    if (buttonsAreAboveViewport) {
      this.#showStickyBar();
      return;
    }

    this.#hideStickyBar();
  };

  #handleViewportChange = () => {
    window.requestAnimationFrame(() => {
      this.#syncStickyVisibility();
    });
  };

  /**
   * Forwards the sticky add-to-cart click to the original
   * product form button.
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) {
      return;
    }

    const stickyButton =
      /** @type {HTMLButtonElement | null} */ (
        this.querySelector(
          '[ref="addToCartButton"]'
        )
      );

    this.#targetAddToCartButton.dataset.puppet =
      'true';

    this.#targetAddToCartButton.click();

    if (
      stickyButton &&
      stickyButton.dataset.added !== 'true'
    ) {
      stickyButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const cartIcon =
      document.querySelector(
        '.header-actions__cart-icon'
      );

    const productImage =
      /** @type {HTMLImageElement | null} */ (
        this.querySelector(
          '[ref="productImage"]'
        )
      );

    if (
      cartIcon &&
      stickyButton &&
      productImage
    ) {
      const flyToCartElement =
        /** @type {FlyToCart} */ (
          document.createElement(
            'fly-to-cart'
          )
        );

      flyToCartElement.classList.add(
        'fly-to-cart--sticky'
      );

      flyToCartElement.style.setProperty(
        'background-image',
        `url(${productImage.src})`
      );

      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(
        flyToCartElement
      );

      await onAnimationEnd([
        stickyButton,
        flyToCartElement,
      ]);
    }

    this.#resetTimeout = setTimeout(() => {
      stickyButton?.removeAttribute(
        'data-added'
      );
    }, 800);
  };

  /**
   * Handles product variant changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    if (
      !event.promise ||
      typeof event.promise.then !== 'function'
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) {
          return;
        }

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          String(productId) !==
            String(this.dataset.productId)
        ) {
          return;
        }

        const newStickyAddToCart =
          /** @type {HTMLElement | null} */ (
            html.querySelector(
              'sticky-add-to-cart'
            )
          );

        if (!newStickyAddToCart) {
          return;
        }

        const newStickyBar =
          newStickyAddToCart.querySelector(
            '[ref="stickyBar"]'
          );

        if (!newStickyBar) {
          return;
        }

        const currentStuck =
          this.refs.stickyBar.getAttribute(
            'data-stuck'
          ) || 'false';

        const variantAvailable =
          newStickyAddToCart.dataset
            .variantAvailable || 'false';

        const nextVariantId =
          variant?.id ||
          newStickyAddToCart.dataset
            .currentVariantId ||
          '';

        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true,
          }
        );

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        this.dataset.variantAvailable =
          variantAvailable;

        this.dataset.currentVariantId =
          String(nextVariantId);

        const productForm =
          this.#getProductForm();

        if (productForm) {
          this.#targetAddToCartButton =
            productForm.querySelector(
              '[ref="addToCartButton"]'
            );
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }

        this.#updateButtonText();
        this.#syncExpressCheckoutInputs();
        this.#initializeAcceleratedCheckout();

        window.requestAnimationFrame(() => {
          this.#syncStickyVisibility();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Updates the option title when no valid variant exists.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId =
      this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }

    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map(
          (option) =>
            /** @type {HTMLInputElement} */ (
              option
            ).value
        )
        .filter((value) => value !== '')
        .join(' / ');

    if (selectedOptions) {
      variantTitleElement.textContent =
        selectedOptions;
    }

    this.#syncExpressCheckoutInputs();
  };

  /**
   * Resets the original form puppet flag after the cart
   * request completes.
   *
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset
          .puppet = 'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Keeps the displayed quantity and accelerated checkout
   * quantity synchronized with the main product selector.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) {
      return;
    }

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
    this.#syncExpressCheckoutInputs();
  };

  #showStickyBar() {
    if (this.#isStuck) {
      return;
    }

    this.#isStuck = true;

    this.refs.stickyBar.dataset.stuck =
      'true';
  }

  #hideStickyBar() {
    if (!this.#isStuck) {
      return;
    }

    this.#isStuck = false;

    this.refs.stickyBar.dataset.stuck =
      'false';
  }

  /**
   * Gets the original product form component, rather than
   * the separate sticky accelerated-checkout form.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) {
      return null;
    }

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) {
      return null;
    }

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1',
        10
      ) || 1;

    this.#updateButtonText();
  }

  #updateButtonText() {
    const addToCartButton =
      /** @type {HTMLButtonElement | null} */ (
        this.querySelector(
          '[ref="addToCartButton"]'
        )
      );

    const quantityDisplay =
      /** @type {HTMLElement | null} */ (
        this.querySelector(
          '[ref="quantityDisplay"]'
        )
      );

    const quantityNumber =
      /** @type {HTMLElement | null} */ (
        this.querySelector(
          '[ref="quantityNumber"]'
        )
      );

    if (
      !addToCartButton ||
      !quantityDisplay ||
      !quantityNumber
    ) {
      return;
    }

    const available =
      !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    quantityDisplay.style.display =
      available &&
      this.#currentQuantity > 1
        ? 'inline'
        : 'none';
  }

  /**
   * Synchronizes the hidden variant and quantity fields used
   * by Shopify accelerated checkout.
   */
  #syncExpressCheckoutInputs() {
    const variantInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector(
          '[ref="buyNowVariant"]'
        )
      );

    const quantityInput =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector(
          '[ref="buyNowQuantity"]'
        )
      );

    if (variantInput) {
      variantInput.value =
        this.dataset.currentVariantId || '';
    }

    if (quantityInput) {
      quantityInput.value =
        this.#currentQuantity.toString();
    }
  }

  /**
   * Current accelerated checkout buttons use Shopify web
   * components. The fallback call also supports older
   * payment-button implementations.
   */
  #initializeAcceleratedCheckout() {
    window.requestAnimationFrame(() => {
      if (
        window.Shopify?.PaymentButton?.init
      ) {
        window.Shopify.PaymentButton.init();
      }
    });
  }
}

if (
  !customElements.get(
    'sticky-add-to-cart'
  )
) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}